import React, { useState, useEffect, useContext, useRef } from 'react';
import './ProfileModal.css';
import Modal from '../Modal/Modal';
import TextInput from '../TextInput/TextInput';
import PhoneInput from '../PhoneInput/PhoneInput';
import EmailInput from '../EmailInput/EmailInput';
import ActionButtons from '../ActionButtons/ActionButtons';
import convenienceApi from '../../common/api/convenience';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import authentication from '../../B2CAuth';
import ChangeEmailModal from '../ChangeEmailModal/ChangeEmailModal';
import logComponent from '../../common/logComponent';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
//import { useMsal } from "@azure/msal-react";

export default function ProfileModal({ hide, isVisible }) {
  //const { instance } = useMsal();
  const environmentContext = useContext(EnvironmentContext);
  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState('initial');
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [updatedUser, setUpdatedUser] = useState({
    LegalFirstName: null,
    PreferredFirstName: null,
    LastName: null,
    EmailAddress: null,
    PhoneNumber: null
  });
  const updatedUserRef = useRef({
    LegalFirstName: null,
    PreferredFirstName: null,
    LastName: null,
    EmailAddress: null,
    PhoneNumber: null
  });

  const setUpdatedUserRef = (el) => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal setUpdatedUserRef'); }
    updatedUserRef.current = el;
  }

  const { activeUser: user, hasToken, selectedImpersonationUser } = environmentContext;
  const formIsInvalid = isValid === false;
  const emailChanged = user.emailAddress && updatedUser.emailAddress && user.emailAddress !== updatedUser.emailAddress.value;

  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const actionButtons = useRef(null);

  if (logComponent('ProfileModal')) { console.debug('ProfileModal rendered'); }


  useEffect(() => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal useEffect [status]'); }
    // handle submission failure
    if (status === 'failure') {
      modalRef.current.scrollTop = 0;
    }
  }, [status])

  const showValidationErrors = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal showValidationErrors'); }
    setShowErrors(true);
  }

  const hideValidationErrors = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal hideValidationErrors'); }
    setShowErrors(false);
  }

  const validateField = (field) => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal validateField'); }
    let newUpdatedUser = { ...updatedUserRef.current };
    newUpdatedUser[lowerCaseFirstLetter(field.name)] = field;
    setUpdatedUserRef(newUpdatedUser);
    setUpdatedUser(newUpdatedUser);
  }

  const changeEmailConfirm = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal changeEmailConfirm'); }
    setShowChangeEmailModal(false);
    handleValidSubmission();
  }

  const changeEmailCancel = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal changeEmailCancel'); }
    setShowChangeEmailModal(false);
  }

  const submit = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal submit'); }
    if (!hasToken) return;

    let emailChanged = user.emailAddress && updatedUser.emailAddress && user.emailAddress !== updatedUser.emailAddress.value;

    //waiting for a second so it will get the last validate field 
    setTimeout(() => {
      let requiredFieldList = getRequiredFieldList();

      if (!requiredFieldList) {
        handleInvalidSubmission();
        return;
      }

      let isValid = requiredFieldList.reduce((prev, curr) => prev && updatedUser[curr] && updatedUser[curr].isValid, true);

      if (!isValid) {
        handleInvalidSubmission();
      } else {

        if (emailChanged) {
          setShowChangeEmailModal(true);

        } else {
          handleValidSubmission();
        }
      }

    }, 1000);

  }

  const handleValidSubmission = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal handleValidSubmission'); }
    hideValidationErrors();
    updateUser();
    setIsValid(true);
  }

  const handleInvalidSubmission = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal handleInvalidSubmission'); }

    showValidationErrors();
    modalRef.current.scrollTop = 0;
    focusFirstItem();
    setIsValid(false);
    reset(); // necessary?
  }

  const getRequiredFieldList = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal getRequiredFieldList'); }

    return ['legalFirstName', 'lastName', 'emailAddress', 'phoneNumber'];
  }

  const updateUser = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal updateUser'); }

    if (!hasToken) return;

    let userToSubmit = {};
    let emailChanged = user.emailAddress && updatedUser.emailAddress && user.emailAddress !== updatedUser.emailAddress.value;

    setStatus('pending');

    let updatedUserEntries = Object.entries(updatedUser);

    updatedUserEntries.forEach(entry => {
      userToSubmit[entry[0]] = entry[1] ? entry[1].value : null;
    });
    // Add/Modify fields as necessary
    userToSubmit.userId = user.userId;
    userToSubmit.status = user.status;
    userToSubmit.isAdmin = user.isAdmin;
    userToSubmit.canCorrespond = user.canCorrespond;
    userToSubmit.facilityId = user.facilityId
    userToSubmit.corporationId = user.corporationId
    userToSubmit.statusSolutionsId = user.statusSolutionsId;
    userToSubmit.externalId = user.externalId;
    userToSubmit.createdBy = user.createdBy;
    userToSubmit.createdOn = user.createdOn;
    userToSubmit.lastModifiedBy = user.lastModifiedBy;
    userToSubmit.lastModifiedOn = user.lastModifiedOn;
    userToSubmit.monitoredIndividualUsers = user.monitoredIndividualUsers;

    convenienceApi.updateUser(user, userToSubmit)
      .then(
        result => {
          setStatus('success');
          if (emailChanged) {
            authentication.signOut();
            //instance.signOut();
          } else {
            if (selectedImpersonationUser) {
              environmentContext.setSelectedImpersonationUser(result);
            } else {
              environmentContext.setUser(result);
            }

            startModalExit();
          }
        },
        error => {
          setStatus('failure');
        }
      )
  }

  const reset = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal reset'); }
    setStatus('initial');
  }

  const focusFirstItem = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal focusFirstItem'); }
    if (firstTabRef && firstTabRef.current) firstTabRef.current.focus();
  }

  const handleShiftTab = (e) => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    if (actionButtons && actionButtons.current) actionButtons.current.focus();
  }

  const startModalExit = () => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal startModalExit'); }
    setModalShouldExit(true);
  }

  const handleKeyDown = (e) => {
    if (logComponent('ProfileModal')) { console.debug('ProfileModal handleKeyDown'); }
    if (e.key === 'Escape') startModalExit();
  }

  return (
    <Modal isVisible={isVisible} shouldExit={modalShouldExit} hide={hide} ref={modalRef} remainOnBackdropClick={true}>
      <div
        className="Profile-Modal"
        onKeyDown={handleKeyDown}>

        <h3 className="Profile-Modal__heading">My Profile</h3>
        <p className={'Profile-Modal__page-error' + (showErrors ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={'Profile-Modal__page-error' + (status === 'failure' ? ' visible' : '')}>Sorry, something went wrong.  Changes were not saved.  Please try again.</p>

        <form className="Profile-Modal__form">
          <TextInput
            initialValue={user.legalFirstName}
            label="First name (legal)"
            name="legalFirstName"
            rules={{ required: true }}
            errorMessages={{ required: 'Please provide your legal first name' }}
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField}
            ref={firstTabRef}
            onKeyDown={handleShiftTab} />
          <TextInput
            initialValue={user.preferredFirstName}
            label="First name (preferred)"
            name="preferredFirstName"
            instructions="If applicable"
            validate={validating}
            onValidate={validateField} />
          <TextInput
            initialValue={user.lastName}
            label="Last name"
            name="lastName"
            rules={{ required: true }}
            errorMessages={{ required: 'Please provide your last name' }}
            showError={!isValid}
            validate={validating}
            onValidate={validateField} />
          <EmailInput
            initialValue={user.emailAddress}
            label="Email address"
            name="emailAddress"
            tabIndex={-1}
            rules={{ required: true }}
            errorMessages={[
              {
                rules: ['required', 'validEmail'],
                text: 'Please provide a valid email address'
              },
              {
                rules: ['unusedEmail'],
                text: 'This email address is already in use'
              }
            ]}
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField} 
            requireEmailUniqueness={ true } />
          <PhoneInput
            initialValue={user.phoneNumber}
            label="Phone number"
            name="phoneNumber"
            rules={{ maxLength: 10, required: true }}
            errorMessage="Please provide a valid phone number"
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField} />

          <ActionButtons
            mainAction={submit}
            mainActionText="Save changes"
            mainActionDisabled={false}
            cancel={startModalExit}
            cancelText="Cancel"
            currentStatus={status}
            ref={actionButtons} />
          <span
            tabIndex="0"
            className="Profile-Modal__focus-control"
            onFocus={focusFirstItem}></span>
        </form>

        {showChangeEmailModal &&
          <ChangeEmailModal
            isCurrentUser={true}
            onChangeEmail={changeEmailConfirm}
            onCancelChangeEmail={changeEmailCancel} />
        }
      </div>
    </Modal>
  );
}